import { useState, useEffect, useCallback } from "react";
import {
  fetchExpenses,
  fetchExpenseSummary,
  createExpense,
  updateExpense,
  deleteExpense,
} from "./expenseService";
import "../payments/paymentDashboard.css";

// ── Constants ─────────────────────────────────────────────────
const CATEGORIES   = ["HOSTING", "ADS", "TOOLS", "SALARY", "CONTENT", "MISC", "OTHERS"];
const PAYMENT_MODES = ["UPI", "CARD", "BANK_TRANSFER", "CASH"];

const CAT_BADGE = {
  HOSTING: "pd-badge-blue",
  ADS:     "pd-badge-amber",
  TOOLS:   "pd-badge-purple",
  SALARY:  "pd-badge-green",
  CONTENT: "pd-badge-red",   // pink-ish using red
  MISC:    "pd-badge-gray",
  OTHERS:  "pd-badge-gray",
};

const TABS = ["Expenses", "Summary"];

// ── Helpers ───────────────────────────────────────────────────
const fmtAmt  = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const emptyForm = () => ({
  title: "", category: "HOSTING", amount: "", expenseDate: "",
  vendor: "", paymentMode: "", description: "", receiptUrl: "",
});

// ── Small shared components ───────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`pd-toast ${type === "error" ? "pd-toast-error" : "pd-toast-success"}`}>{msg}</div>;
};

const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div className="pd-overlay">
    <div className="pd-modal pd-modal-sm">
      <p className="pd-confirm-msg">{message}</p>
      <div className="pd-modal-actions">
        <button className="pd-btn pd-btn-danger" onClick={onConfirm}>Delete</button>
        <button className="pd-btn pd-btn-ghost"  onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

// ── Add / Edit Modal ──────────────────────────────────────────
const ExpenseModal = ({ initial, onClose, onSuccess }) => {
  const [form,   setForm]   = useState(initial || emptyForm());
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim())    return "Title is required.";
    if (!form.category)        return "Category is required.";
    if (!form.amount || Number(form.amount) <= 0) return "Amount must be a positive number.";
    if (!form.expenseDate)     return "Expense date is required.";
    return null;
  };

  const save = async () => {
    const vErr = validate();
    if (vErr) { setErr(vErr); return; }
    setSaving(true); setErr("");
    try {
      const body = {
        title: form.title.trim(),
        category: form.category,
        amount: Number(form.amount),
        expenseDate: form.expenseDate,
        ...(form.vendor      && { vendor: form.vendor.trim() }),
        ...(form.paymentMode && { paymentMode: form.paymentMode }),
        ...(form.description && { description: form.description.trim() }),
        ...(form.receiptUrl  && { receiptUrl: form.receiptUrl.trim() }),
      };
      if (initial?.id) await updateExpense(initial.id, body);
      else             await createExpense(body);
      onSuccess(initial?.id ? "Expense updated." : "Expense added.");
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to save expense.");
    } finally { setSaving(false); }
  };

  const isEdit = !!initial?.id;

  return (
    <div className="pd-overlay">
      <div className="pd-modal" style={{ maxWidth: 520 }}>
        <h3 className="pd-modal-title">{isEdit ? "Edit Expense" : "Add Expense"}</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Title *</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. AWS Hosting - June" />
          </div>

          <div className="pd-form-group">
            <label>Category *</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="pd-form-group">
            <label>Amount (₹) *</label>
            <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" />
          </div>

          <div className="pd-form-group">
            <label>Expense Date *</label>
            <input type="date" value={form.expenseDate} onChange={(e) => set("expenseDate", e.target.value)} />
          </div>

          <div className="pd-form-group">
            <label>Payment Mode</label>
            <select value={form.paymentMode} onChange={(e) => set("paymentMode", e.target.value)}>
              <option value="">— Select —</option>
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
            </select>
          </div>

          <div className="pd-form-group">
            <label>Vendor</label>
            <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="e.g. Amazon AWS" />
          </div>

          <div className="pd-form-group">
            <label>Receipt URL</label>
            <input value={form.receiptUrl} onChange={(e) => set("receiptUrl", e.target.value)} placeholder="https://..." />
          </div>

          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              placeholder="Optional notes…"
              style={{ padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", resize: "vertical", outline: "none", fontFamily: "inherit" }}
            />
          </div>
        </div>

        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          <button className="pd-btn pd-btn-ghost"   onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Summary Tab ───────────────────────────────────────────────
const SummaryTab = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExpenseSummary()
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="pd-loading">Loading summary…</div>;
  if (!data)   return <div className="pd-empty">No summary data.</div>;

  const maxCat = Math.max(...(data.byCategory?.map((c) => c.total) || [1]), 1);
  const maxMon = Math.max(...(data.byMonth?.map((m) => m.total) || [1]), 1);

  return (
    <div style={{ padding: "1rem 1.5rem" }}>
      {/* Total */}
      <div className="pd-page-total-bar" style={{ marginBottom: "1.25rem" }}>
        <span className="pd-page-total-label">💰 Total Overall Spend</span>
        <span className="pd-page-total-value">{fmtAmt(data.totalOverall)}</span>
      </div>

      {/* By Category */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#374151", marginBottom: "0.75rem" }}>Spend by Category</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
          {data.byCategory?.map((c) => (
            <div key={c.category} className="pd-stat-card">
              <div className="pd-stat-label">
                <span className={`pd-badge ${CAT_BADGE[c.category] || "pd-badge-gray"}`}>{c.category}</span>
              </div>
              <div className="pd-stat-value" style={{ marginTop: 6 }}>{fmtAmt(c.total)}</div>
            </div>
          ))}
        </div>
        {/* Bar chart */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
          {data.byCategory?.filter((c) => c.total > 0).map((c) => (
            <div key={c.category} style={{ marginBottom: "0.6rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem", marginBottom: 3 }}>
                <span className={`pd-badge ${CAT_BADGE[c.category] || "pd-badge-gray"}`}>{c.category}</span>
                <span style={{ fontWeight: 700, color: "#0f172a" }}>{fmtAmt(c.total)}</span>
              </div>
              <div style={{ background: "#f1f5f9", borderRadius: 4, height: 10 }}>
                <div style={{ width: `${(c.total / maxCat) * 100}%`, background: "#019095", borderRadius: 4, height: "100%", transition: "width 0.4s" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly trend */}
      {data.byMonth?.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#374151", marginBottom: "0.75rem" }}>Monthly Trend</div>
          <div style={{ background: "#fff", borderRadius: 10, padding: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
            {[...data.byMonth].sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month).map((m) => {
              const label = new Date(m.year, m.month - 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
              return (
                <div key={`${m.year}-${m.month}`} style={{ marginBottom: "0.6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem", marginBottom: 3 }}>
                    <span style={{ color: "#64748b", fontWeight: 600 }}>{label}</span>
                    <span style={{ fontWeight: 700, color: "#0f172a" }}>{fmtAmt(m.total)}</span>
                  </div>
                  <div style={{ background: "#f1f5f9", borderRadius: 4, height: 10 }}>
                    <div style={{ width: `${(m.total / maxMon) * 100}%`, background: "#6d28d9", borderRadius: 4, height: "100%", transition: "width 0.4s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const ExpenseDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [toast,     setToast]     = useState(null);
  const [modal,     setModal]     = useState(null);   // null | "add" | rowObj (edit)
  const [confirm,   setConfirm]   = useState(null);   // null | { message, action }

  // filters
  const [category,  setCategory]  = useState("");
  const [fromDate,  setFromDate]  = useState("");
  const [toDate,    setToDate]    = useState("");

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (category) params.category = category;
      if (fromDate) params.from = fromDate;
      if (toDate)   params.to   = toDate;
      const res = await fetchExpenses(params);
      setRows(res.data || []);
    } catch { setError("Failed to load expenses."); }
    finally  { setLoading(false); }
  }, [category, fromDate, toDate]);

  useEffect(() => { if (activeTab === 0) load(); }, [activeTab, load]);

  const handleApply = () => load();
  const handleClear = () => { setCategory(""); setFromDate(""); setToDate(""); };

  const afterSave = (msg) => {
    setModal(null);
    showToast(msg);
    load();
  };

  const handleDelete = (row) => {
    setConfirm({
      message: `Delete "${row.title}"? This cannot be undone.`,
      action: async () => {
        try {
          await deleteExpense(row.id);
          setConfirm(null);
          showToast("Expense deleted.");
          load();
        } catch { showToast("Failed to delete.", "error"); setConfirm(null); }
      },
    });
  };

  const totalShown = rows.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="pd-page">
      {toast   && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && <ConfirmDialog message={confirm.message} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {modal   && (
        <ExpenseModal
          initial={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={afterSave}
        />
      )}

      <div className="pd-header">
        <h2 className="pd-title">Expense Management</h2>
        {activeTab === 0 && (
          <button className="pd-btn pd-btn-primary" onClick={() => setModal("add")}>+ Add Expense</button>
        )}
      </div>

      {/* Tabs */}
      <div className="pd-tabs">
        {TABS.map((t, i) => (
          <button key={t} className={`pd-tab ${activeTab === i ? "pd-tab-active" : ""}`} onClick={() => setActiveTab(i)}>{t}</button>
        ))}
      </div>

      {activeTab === 1 ? (
        <SummaryTab />
      ) : (
        <>
          {/* Filter bar */}
          <div className="pd-filter-bar">
            <select className="pd-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className="pd-input pd-input-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From date" />
            <input className="pd-input pd-input-date" type="date" value={toDate}   onChange={(e) => setToDate(e.target.value)}   title="To date" />
            <button className="pd-btn pd-btn-primary pd-btn-sm" onClick={handleApply}>Apply</button>
            <button className="pd-btn pd-btn-ghost pd-btn-sm"   onClick={handleClear}>Clear</button>
          </div>

          {error && <div className="pd-error-bar">{error}</div>}

          <div className="pd-table-area">
            {loading ? (
              <div className="pd-loading">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="pd-empty">
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>💸</div>
                <div>No expenses found. Add your first expense!</div>
              </div>
            ) : (
              <>
                <div className="pd-page-total-bar">
                  <span className="pd-page-total-label">📄 Showing {rows.length} record{rows.length !== 1 ? "s" : ""}</span>
                  <span className="pd-page-total-value">{fmtAmt(totalShown)}</span>
                </div>
                <div className="pd-count">{rows.length} expense{rows.length !== 1 ? "s" : ""}</div>
                <div className="pd-table-wrap">
                  <table className="pd-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Vendor</th>
                        <th>Payment Mode</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <div className="pd-cell-bold">{r.title}</div>
                            {r.description && <div className="pd-cell-sub" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.description}</div>}
                          </td>
                          <td>
                            <span className={`pd-badge ${CAT_BADGE[r.category] || "pd-badge-gray"}`}>{r.category}</span>
                          </td>
                          <td className="pd-cell-amount">{fmtAmt(r.amount)}</td>
                          <td className="pd-cell-sub">{fmtDate(r.expenseDate)}</td>
                          <td className="pd-cell-sub">{r.vendor || "—"}</td>
                          <td className="pd-cell-sub">{r.paymentMode ? r.paymentMode.replace("_", " ") : "—"}</td>
                          <td>
                            <div className="pd-cell-actions">
                              <button className="pd-btn pd-btn-ghost pd-btn-sm"        onClick={() => setModal(r)}>✏️</button>
                              <button className="pd-btn pd-btn-danger-ghost pd-btn-sm" onClick={() => handleDelete(r)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseDashboard;
